import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getDashboard } from '../../services/portal'

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

function fmtMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`
}

export default function PortalDashboardPage() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['portal', 'dashboard'], queryFn: getDashboard })

  if (isLoading) return <div className="py-12 text-center text-sm text-nala-mute">Loading…</div>
  if (isError) {
    return (
      <div className="py-12 text-center text-sm text-nala-mute">
        Unable to load. Please refresh or sign in again.
      </div>
    )
  }
  if (!data) return <div className="py-12 text-center text-sm text-nala-mute">No data available.</div>

  const firstName = data.user.first_name

  return (
    <div className="space-y-8">
      {/* Page heading */}
      <div>
        <h1 className="font-fraunces text-4xl font-[400] tracking-tight text-nala-ink leading-tight">
          {firstName ? <>Hello, {firstName}.</> : 'Welcome back.'}
        </h1>
        <p className="text-nala-mute mt-1.5 text-[15px]">
          Here's everything happening with your pets.
        </p>
      </div>

      {/* Profile completion nudge */}
      {!data.user.profile_complete && (
        <div className="bg-white border border-nala-line rounded-2xl p-5 flex items-start gap-4">
          <div className="w-9 h-9 rounded-xl bg-nala-soft flex items-center justify-center text-lg shrink-0">📝</div>
          <div className="flex-1">
            <h3 className="font-semibold text-nala-ink text-sm">Finish setting up your profile</h3>
            <p className="text-sm text-nala-mute mt-0.5">
              Add your name and phone number so your vet can reach you on appointment day.
            </p>
          </div>
          <Link to="/profile" className="text-sm font-semibold text-nala-clay hover:underline whitespace-nowrap shrink-0">
            Update →
          </Link>
        </div>
      )}

      {/* Next appointment hero */}
      {data.next_appointment ? (
        <div className="bg-nala-accent rounded-3xl p-7 text-nala-accentInk relative overflow-hidden">
          {/* subtle texture */}
          <div className="absolute inset-0 opacity-10 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, #ffffff 0%, transparent 60%)' }} />

          <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-widest text-nala-accentInk/70 mb-2">
              Your next visit
            </p>
            <h2 className="font-fraunces text-3xl font-[400] leading-tight">
              {data.next_appointment.service_name || 'House call'}
            </h2>
            <p className="mt-2 text-nala-accentInk/80 text-[15px]">
              {fmtDate(data.next_appointment.scheduled_at)}
            </p>
            {data.next_appointment.veterinarian_name && (
              <p className="text-nala-accentInk/70 text-sm mt-1">
                with {data.next_appointment.veterinarian_name}
              </p>
            )}
            {data.next_appointment.address_one_line && (
              <p className="text-nala-accentInk/70 text-sm mt-0.5">
                {data.next_appointment.address_one_line}
              </p>
            )}

            <div className="mt-6 flex items-center gap-3 flex-wrap">
              {data.next_appointment.trackable && data.next_appointment.track_token ? (
                <Link
                  to={`/portal/track/${data.next_appointment.track_token}`}
                  className="inline-flex items-center gap-2 bg-white text-nala-accent font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-nala-surface transition"
                >
                  📍 Track your vet
                </Link>
              ) : (
                <Link
                  to={`/portal/appointments/${data.next_appointment.id}`}
                  className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-nala-accentInk font-semibold rounded-full px-5 py-2.5 text-sm transition"
                >
                  View details
                </Link>
              )}
              <span className="px-3 py-1.5 rounded-full bg-white/10 text-xs uppercase tracking-wider">
                {data.next_appointment.status}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-nala-line rounded-3xl p-10 text-center">
          <div className="w-16 h-16 rounded-2xl bg-nala-soft flex items-center justify-center text-3xl mx-auto mb-4">📅</div>
          <h3 className="font-fraunces text-2xl font-[400] text-nala-ink">No upcoming visits</h3>
          <p className="text-nala-mute mt-1.5 mb-6 text-sm max-w-xs mx-auto">
            Book your next house call and get the whole clinic experience from your couch.
          </p>
          <Link
            to="/book"
            className="inline-flex items-center gap-2 bg-nala-accent hover:bg-nala-accent/90 text-nala-accentInk font-semibold rounded-full px-6 py-3 text-sm transition"
          >
            Book a visit
          </Link>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Pets"
          value={data.counts.pets}
          sub="in your household"
          link="/portal/pets"
          emoji="🐾"
        />
        <StatCard
          label="Upcoming visits"
          value={data.counts.upcoming_appointments}
          sub="scheduled"
          link="/portal/appointments"
          emoji="📅"
        />
        <StatCard
          label="Outstanding"
          value={fmtMoney(data.outstanding_balance_cents)}
          sub={data.outstanding_balance_cents > 0 ? 'balance due' : 'all clear'}
          link="/portal/invoices"
          emoji="🧾"
          highlight={data.outstanding_balance_cents > 0}
        />
      </div>

      {/* Recent invoices */}
      {data.recent_invoices.length > 0 && (
        <div className="bg-white border border-nala-line rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-nala-line">
            <h3 className="font-fraunces text-lg font-[500] text-nala-ink">Recent invoices</h3>
            <Link to="/portal/invoices" className="text-sm text-nala-clay font-semibold hover:underline">
              View all →
            </Link>
          </div>
          <ul className="divide-y divide-nala-line">
            {data.recent_invoices.map((inv) => (
              <li key={inv.id} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="font-medium text-nala-ink text-sm">{inv.number}</div>
                  <div className="text-xs text-nala-mute mt-0.5">
                    {inv.due_at ? `Due ${new Date(inv.due_at).toLocaleDateString()}` : 'No due date'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-fraunces font-[500] text-nala-ink">{fmtMoney(inv.total_cents)}</div>
                  <div className={`text-xs uppercase tracking-wide mt-0.5 ${inv.overdue ? 'text-nala-clay' : 'text-nala-mute'}`}>
                    {inv.overdue ? 'Overdue' : inv.status}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value, sub, link, emoji, highlight }: {
  label: string
  value: number | string
  sub: string
  link: string
  emoji: string
  highlight?: boolean
}) {
  return (
    <Link
      to={link}
      className={`bg-white border rounded-2xl p-5 hover:shadow-md transition-shadow flex items-center gap-4 group ${
        highlight ? 'border-nala-clay/40' : 'border-nala-line'
      }`}
    >
      <div className="w-12 h-12 rounded-xl bg-nala-soft flex items-center justify-center text-2xl shrink-0">
        {emoji}
      </div>
      <div>
        <div className={`font-fraunces text-2xl font-[500] leading-none ${highlight ? 'text-nala-clay' : 'text-nala-ink'}`}>
          {value}
        </div>
        <div className="text-xs text-nala-mute mt-1">{label}</div>
        <div className="text-[11px] text-nala-line group-hover:text-nala-mute transition-colors">{sub}</div>
      </div>
    </Link>
  )
}
